// Sales tickets from every channel, newest first (plan §5.4): trading day, channel, the
// channel's own receipt number, type and status, totals and VAT. Above them, the totals of
// everything the filters match (not just the page), and filters for the receipt, channel,
// status, type and trading days (review UI2A-5). A sales replay's tickets are never here
// (they belong to the replay, D-036).

import type { SaleTicketSummary } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import {
  Button,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  Input,
  Money,
  PageHeader,
  Skeleton,
  type ColumnDef,
} from '@enp/ui';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, ReceiptText, Search, SearchX, TriangleAlert, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { TicketStatusBadges, TicketTypeBadge, ticketLabel } from '../../../sales/components.js';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { usePageSearch } from '../../../shell/signals.js';
import { DayRange, FilterSelect, LoadMoreFooter } from '../../../stock/components.js';
import {
  ORDER_TYPE_LABEL,
  plainDateLabel,
  TICKET_STATUS,
  TICKET_TYPE_LABEL,
  accountToday,
} from '../../../stock/labels.js';
import {
  clean,
  salesChannelsQuery,
  salesSummaryQuery,
  type TicketsSearch,
  ticketsQuery,
  ticketsSearchSchema,
} from '../../../stock/queries.js';

export const Route = createFileRoute('/_app/sales/')({
  validateSearch: ticketsSearchSchema,
  component: SalesPage,
});

const TABLE_LABEL = 'Sales tickets';

const plural = (n: number, one: string, many: string) =>
  `${formatCount(n)} ${n === 1 ? one : many}`;

function ticketColumns(today: string): ColumnDef<SaleTicketSummary, unknown>[] {
  const col = createColumnHelper<SaleTicketSummary>();
  const cancelled = (t: SaleTicketSummary) => t.status === 'cancelled';
  return [
    col.display({
      id: 'when',
      header: 'When',
      cell: ({ row }) => (
        <span className="flex flex-col whitespace-nowrap leading-tight">
          <AccountDate value={row.original.occurred_at} withTime className="text-fg" />
          <span className="text-xs text-muted">
            Trading day {plainDateLabel(row.original.trading_day, today)}
          </span>
        </span>
      ),
    }),
    col.display({
      id: 'receipt',
      header: 'Receipt',
      cell: ({ row }) => (
        <span className="flex flex-col leading-tight">
          <Link
            to="/sales/$ticketId"
            params={{ ticketId: row.original.id }}
            tabIndex={-1}
            className="whitespace-nowrap font-mono text-sm font-medium text-fg hover:underline"
          >
            {ticketLabel(row.original)}
          </Link>
          <span className="text-xs text-muted">
            {ORDER_TYPE_LABEL[row.original.order_type] ?? row.original.order_type}
          </span>
        </span>
      ),
    }),
    col.display({
      id: 'channel',
      header: 'Channel',
      cell: ({ row }) => <span className="whitespace-nowrap">{row.original.channel.name}</span>,
    }),
    col.display({
      // Type and status in their own columns, always as badges (review UI2A-18).
      id: 'type',
      header: 'Type',
      cell: ({ row }) => <TicketTypeBadge ticket={row.original} />,
    }),
    col.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <TicketStatusBadges ticket={row.original} />,
    }),
    col.display({
      id: 'lines',
      header: 'Lines',
      meta: { align: 'right', className: 'hidden lg:table-cell' },
      cell: ({ row }) => (
        <span className="flex flex-col items-end leading-tight">
          <span className="tabular-nums">{row.original.line_count}</span>
          {row.original.unmapped_line_count > 0 && (
            <span className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-state-warning">
              <TriangleAlert aria-hidden className="size-3.5" />
              {plural(row.original.unmapped_line_count, 'line', 'lines')} not in catalogue
            </span>
          )}
        </span>
      ),
    }),
    col.display({
      id: 'total',
      header: 'Total',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Money
          cents={row.original.total_cents}
          className={cancelled(row.original) ? 'text-muted line-through' : 'font-medium'}
        />
      ),
    }),
    col.display({
      id: 'vat',
      header: 'VAT',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Money
          cents={row.original.vat_cents}
          className={cn('text-muted', cancelled(row.original) && 'line-through')}
        />
      ),
    }),
    col.display({
      id: 'open',
      header: () => <span className="sr-only">Open</span>,
      meta: { width: 'w-8' },
      cell: () => <ChevronRight aria-hidden className="size-4 text-muted" />,
    }),
  ];
}

function Figure({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="flex min-w-36 flex-col gap-0.5 px-4 py-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="font-display text-xl font-semibold tabular-nums text-fg">{children}</dd>
      {hint && <dd className="text-xs text-muted">{hint}</dd>}
    </div>
  );
}

/** Totals of everything the filters match (review UI2A-5): not just the rows loaded. */
function SummaryStrip({ search, filtered }: { search: TicketsSearch; filtered: boolean }) {
  const summary = useQuery({ ...salesSummaryQuery(search), placeholderData: keepPreviousData });
  const s = summary.data;
  return (
    <section
      aria-label={filtered ? 'Totals of the tickets shown' : 'Totals of all tickets'}
      aria-busy={summary.isPending || undefined}
      className="mb-4 rounded-lg border border-border bg-surface-raised shadow-xs"
    >
      {summary.isError && !s ? (
        <p className="px-4 py-3 text-sm text-muted" role="status">
          The totals didn’t load.{' '}
          <button
            type="button"
            onClick={() => void summary.refetch()}
            className="font-medium text-primary-text underline underline-offset-2"
          >
            Try again
          </button>
        </p>
      ) : !s ? (
        <div className="flex flex-wrap gap-6 px-4 py-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-11 w-32" />
          ))}
        </div>
      ) : (
        <dl className="flex flex-wrap divide-x divide-border">
          <Figure
            label="Tickets"
            // A cancelled sale is one of the sales, not a third kind (UI2A-211).
            hint={[
              plural(s.sales, 'sale', 'sales') +
                (s.cancelled > 0 ? ` (${formatCount(s.cancelled)} cancelled)` : ''),
              plural(s.refunds, 'refund', 'refunds'),
              s.open > 0 && `${formatCount(s.open)} open`,
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            {formatCount(s.tickets)}
          </Figure>
          <Figure label="Total" hint="Refunds taken off; cancelled tickets not counted">
            <Money cents={s.total_cents} />
          </Figure>
          <Figure label="VAT" hint="Sum of the lines’ VAT">
            <Money cents={s.vat_cents} />
          </Figure>
          <Figure
            label="Lines not in catalogue"
            hint={s.unmapped_lines > 0 ? 'Sold, but matched to no product' : 'Every line matched'}
          >
            <span
              className={cn(
                'inline-flex items-center gap-1.5',
                s.unmapped_lines > 0 && 'text-state-warning',
              )}
            >
              {s.unmapped_lines > 0 && <TriangleAlert aria-hidden className="size-4" />}
              {formatCount(s.unmapped_lines)}
            </span>
          </Figure>
        </dl>
      )}
    </section>
  );
}

function SalesPage() {
  useDocumentTitle('Sales');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const setSearch = (patch: Partial<TicketsSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });
  const searchRef = useRef<HTMLInputElement>(null);
  usePageSearch(searchRef);

  // The receipt search types freely; the URL (and the server) follow after a pause.
  const [text, setText] = useState(search.q ?? '');
  const pushed = useRef(search.q ?? '');
  useEffect(() => {
    if ((search.q ?? '') !== pushed.current) {
      pushed.current = search.q ?? '';
      setText(search.q ?? '');
    }
  }, [search.q]);
  useEffect(() => {
    const q = text.trim();
    if (q === pushed.current) return;
    const timer = setTimeout(() => {
      pushed.current = q;
      void navigate({ search: (prev) => clean({ ...prev, q: q || undefined }), replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [text, navigate]);

  const list = useInfiniteQuery({ ...ticketsQuery(search), placeholderData: keepPreviousData });
  const channels = useQuery(salesChannelsQuery());
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const columns = useMemo(() => ticketColumns(today), [today]);
  const filtered = Boolean(
    search.from || search.to || search.q || search.channel || search.status || search.type,
  );
  const onlyDays = filtered && !search.q && !search.channel && !search.status && !search.type;
  const firstLoad = list.isPending;
  const failed = list.isError && !list.data;
  const clearAll = () => {
    pushed.current = '';
    setText('');
    void navigate({ search: {} });
  };

  return (
    <>
      <PageHeader
        title="Sales"
        description="Every ticket from your tills and shops, newest first, with VAT worked out per line."
      />
      <div
        className="mb-3 flex flex-wrap items-center gap-2"
        role="search"
        aria-label="Sales filters"
      >
        <div className="relative w-full max-w-52">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
          />
          <Input
            ref={searchRef}
            type="search"
            aria-label="Search receipts"
            aria-keyshortcuts="/"
            placeholder="Receipt number"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="pl-8"
          />
        </div>
        <FilterSelect
          label="Channel"
          value={search.channel}
          allLabel="All channels"
          options={(channels.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
          onChange={(v) => setSearch({ channel: v })}
        />
        <FilterSelect
          label="Type"
          value={search.type}
          allLabel="Sales and refunds"
          options={[
            { value: 'sale', label: `${TICKET_TYPE_LABEL.sale}s only` },
            { value: 'refund', label: `${TICKET_TYPE_LABEL.refund}s only` },
          ]}
          onChange={(v) => setSearch({ type: v as TicketsSearch['type'] })}
        />
        <FilterSelect
          label="Status"
          value={search.status}
          allLabel="Any status"
          options={(['completed', 'cancelled', 'open'] as const).map((s) => ({
            value: s,
            label: TICKET_STATUS[s].label,
          }))}
          onChange={(v) => setSearch({ status: v as TicketsSearch['status'] })}
        />
        <DayRange
          idPrefix="sales"
          from={search.from}
          to={search.to}
          today={today}
          onChange={(r) => setSearch({ from: r.from, to: r.to })}
        />
        {filtered && (
          <Button variant="ghost" icon={<X aria-hidden />} onClick={clearAll}>
            Clear
          </Button>
        )}
      </div>
      {/* No totals over nothing (UI2A-212): the empty state says it. */}
      {!failed && items.length > 0 && <SummaryStrip search={search} filtered={filtered} />}
      {failed ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="Sales"
          onRetry={() => void list.refetch()}
        />
      ) : (
        <DataTable
          label={TABLE_LABEL}
          columns={columns}
          data={items}
          loading={firstLoad}
          filterable={false}
          getRowId={(t) => t.id}
          onRowActivate={(t) =>
            void navigate({ to: '/sales/$ticketId', params: { ticketId: t.id } })
          }
          empty={
            filtered ? (
              <EmptyState
                compact
                icon={SearchX}
                title={onlyDays ? 'No sales on these days' : 'No tickets match'}
                description={
                  onlyDays
                    ? 'Nothing was sold between these trading days. Widen the dates, or clear them.'
                    : 'No ticket fits these filters. Check the receipt number, or clear the filters.'
                }
                action={
                  <Button variant="secondary" icon={<X aria-hidden />} onClick={clearAll}>
                    {onlyDays ? 'Clear dates' : 'Clear filters'}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={ReceiptText}
                title="No sales yet"
                description="Tickets arrive here from your tills once a sales channel is connected. To check your catalogue against past sales first, run a sales replay."
                action={
                  <Button asChild>
                    <Link to="/replay">Open Sales replay</Link>
                  </Button>
                }
              />
            )
          }
          footer={
            !firstLoad && items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                noun={['ticket', 'tickets']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
          className="xl:max-h-[calc(100dvh-24rem)] xl:min-h-64"
        />
      )}
    </>
  );
}
